import AppContext from 'App/AppContext';
import { IPCheckbox, IPLabel, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { FluentUIPieChart, IPieChartDataPoint } from 'components/Charts/PieChart';
import KeyValueTagPicker from 'components/Pickers/KeyValueTagPicker';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import RiskState, { RiskStates } from 'models/riskState';
import Tag from 'models/tag';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataRiskStatus } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { getRiskStateColorGradient } from 'utils/risk';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetRiskStatusNotAcceptedConfig {
  tagIds: number[];

  showTags: boolean;

  tags: Tag[];

  constructor() {
    this.tagIds = [];
    this.showTags = false;
    this.tags = [];
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.tagIds = newRawConfig.tagIds ?? [];
        this.showTags = newRawConfig.showTags ?? false;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetRiskStatusNotAcceptedConfig {
    const newConfig = new WidgetRiskStatusNotAcceptedConfig();
    newConfig.tagIds = [...this.tagIds];
    newConfig.tags = [...this.tags];
    newConfig.showTags = this.showTags;

    return newConfig;
  }
}

interface IWidgetRiskStatusProps extends IWidgetRendererProps {}

const WidgetRiskStatusNotAccepted: FunctionComponent<IWidgetRiskStatusProps> = (props: IWidgetRiskStatusProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [data, setData] = useState<RiskState[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [riskCount, setRiskCount] = useState<number>(0);
  const [config, setConfig] = useState<WidgetRiskStatusNotAcceptedConfig | undefined>(undefined);
  const [colorGradient] = useState<string[]>(getRiskStateColorGradient());

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        await appContext.globalDataCache.tags.getItems();
        const config = loadConfig();
        setConfig(config);

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);

        let data = await apiGetWidgetDataRiskStatus(config.tags, false, accessToken, props.abortSignal);

        data = data
          .filter(
            (risk) =>
              risk.riskStateId !== RiskStates.Accepted &&
              risk.riskStateId !== RiskStates.Rejected &&
              risk.riskStateId !== RiskStates.Archived,
          )
          .sort((a, b) => a.sortOrder - b.sortOrder);

        setRiskCount(data.reduce((sum, current) => sum + (current.riskCount ?? 0), 0));
        setData(data);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetRiskStatusNotAcceptedConfig => {
    const newConfig = new WidgetRiskStatusNotAcceptedConfig();
    newConfig.load(props.widget.widgetConfig);
    newConfig.tags = appContext.globalDataCache.tags.getItemsForId(newConfig.tagIds).filter((t) => t.tagId !== 0);

    return newConfig;
  };

  const getRiskStateColor = (idx: number): string => {
    return colorGradient[idx];
  };

  const getData = (): IPieChartDataPoint[] => {
    if (data.length === 0) return [];

    const config = loadConfig();
    const tagStr = `&tags=${config.tags.map((t) => t.tagId.toString()).join(',')}`;

    const graphData: IPieChartDataPoint[] = data.map((s, idx) => {
      return {
        name: s.state,
        value: s.riskCount ?? 0,
        color: getRiskStateColor(idx),
        link: `/risks/overview?status=NotAccepted${config.tags.length > 0 ? tagStr : ''}`,
      };
    });

    return graphData;
  };

  if (!data || isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (riskCount === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:RiskStatusNotAccepted.NoRisks')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill horizontalAlign="center" tokens={globalStackTokensGapSmall}>
      {config && config.tags.length > 0 && config.showTags && (
        <IPStack.Item styles={{ root: { maxHeight: 44, overflowY: 'hidden' } }}>
          <KeyValueTags tags={config.tags} compact tagMaxWidth={200} />
        </IPStack.Item>
      )}
      <IPStack.Item grow>
        <FluentUIPieChart
          data={getData()}
          height={config && config.tags.length > 0 && config.showTags ? 190 : 250}
          width={280}
          label={riskCount.toString()}
        ></FluentUIPieChart>
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetRiskStatusNotAccepted;

//
// Config
//

interface IWidgetConfigWidgetRiskStatusNotAcceptedProps extends IWidgetConfigRendererProps {}

export const WidgetConfigRiskStatusNotAccepted: FunctionComponent<IWidgetConfigWidgetRiskStatusNotAcceptedProps> = (
  props: IWidgetConfigWidgetRiskStatusNotAcceptedProps,
) => {
  const { t } = useTranslation(['widgets', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [showTags, setShowTags] = useState<boolean>(false);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetRiskStatusNotAcceptedConfig => {
    let newRawConfig = new WidgetRiskStatusNotAcceptedConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
      newRawConfig.tags = appContext.globalDataCache.tags
        .getItemsForId(newRawConfig.tagIds)
        .filter((t) => t.tagId !== 0);
    }

    return newRawConfig;
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();
      const config = loadConfig();

      if (config.tags) {
        setSelectedTags(config.tags);
      }

      setShowTags(config.showTags);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (tags: Tag[], showTags: boolean): WidgetRiskStatusNotAcceptedConfig => {
    const config = new WidgetRiskStatusNotAcceptedConfig();
    config.tagIds = tags.map((t) => t.tagId);
    config.showTags = showTags;

    return config;
  };

  const onAdd = (tag: Tag) => {
    const newTags = [...selectedTags, tag];
    setSelectedTags(newTags);
    const config = getConfig(newTags, showTags);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onRemove = (tag: Tag) => {
    const newTags = selectedTags.filter((t) => t.tagId !== tag.tagId);
    setSelectedTags(newTags);
    const config = getConfig(newTags, showTags);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetShowTags = (checked: boolean) => {
    setShowTags(checked);
    const config = getConfig(selectedTags, checked);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPLabel>{t('widgets:RiskStatusNotAccepted.Config.Label')}</IPLabel>
        <KeyValueTagPicker
          itemLimit={5}
          selectedTags={selectedTags}
          onAdd={onAdd}
          onRemove={onRemove}
          isLoading={isLoading}
          allowCreate={false}
        ></KeyValueTagPicker>
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          checked={showTags}
          label={t('widgets:RiskStatusNotAccepted.Config.LabelShowTags')}
          onChange={(ev, checked) => {
            onSetShowTags(checked ?? false);
          }}
        />
      </IPStack.Item>
    </IPStack>
  );
};
